import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook"; // Use Firestore hook
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const defaultFormData = {
  Credit_Type_abv: "",
  Credit_Type_Name: "",
  Note: "",
  id: "",
};

const Edit = ({ open, onClose, creditTypeDetail, onRecordUpdated }) => {
  // Use Firestore hook with the specified collection name "TpCreditType"
  // Assuming your hook provides updateDocument and deleteDocument
  const { updateData, deleteData } = useFirestore("TpCreditType");
  const [formData, setFormData] = useState(creditTypeDetail || defaultFormData);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    setFormData(creditTypeDetail || defaultFormData);
    // Ensure 'creditTypeDetail' prop includes the Firestore document 'id'
  }, [creditTypeDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing. Cannot update.");
      }
      await updateData(formData.id, formData); // Use updateData with document ID
      setLoading(false);
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing. Cannot delete.");
      }
      await deleteData(formData.id); // Use deleteData with document ID
      setLoading(false);
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
      onClose(); // Close the dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Credit Type Abv"
                name="Credit_Type_abv"
                value={formData.Credit_Type_abv}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Credit Type Name"
                name="Credit_Type_Name"
                value={formData.Credit_Type_Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Note"
                name="Note"
                value={formData.Note}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message="Record has been updated"
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          ContentProps={{
            sx: {
              backgroundColor: "black",
              color: "white",
            },
          }}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
